import { Injectable } from '@angular/core';
import { LocalStorageFields } from '@shared/config/localstorage.config';

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  public static isLoggedIn(): boolean {
    return localStorage.getItem(LocalStorageFields.IS_LOGGED_IN) === '1';
  }

  public static login() {
    LocalStorageService.doLogAction('1');
  }

  public static logout() {
    LocalStorageService.doLogAction('0');
  }

  private static doLogAction(logAction: string) {
    localStorage.setItem(LocalStorageFields.IS_LOGGED_IN, logAction);
  }

  /**
   * Remove a specified item of the local storage
   *
   * @param item LocalStorageFields key to remove
   */
  public static removeItem(item: LocalStorageFields) {
    if (!Object.values(LocalStorageFields).includes(item)) {
      return;
    }

    localStorage.removeItem(item);
  }

  public static cleanCookies() {
    this.logout();
    this.setAuthorities('');
    this.setUserEmail('');
    this.setGroups('');
    this.setApplications('');
    localStorage.removeItem(LocalStorageFields.THEME);
    localStorage.removeItem(LocalStorageFields.USER_SETTINGS_ID);
    localStorage.removeItem(LocalStorageFields.KEEP_ALIVE);
    localStorage.removeItem(LocalStorageFields.CHATBOT_AGENT);
    this.cleanMsalItems();
  }

  /**
   * Clean the application MSAL items
   */
  public static cleanMsalItems() {
    LocalStorageService.removeItem(LocalStorageFields.MSAL_LOGIN_HINT);
  }

  public static setAuthorities(authorities: string) {
    localStorage.setItem(LocalStorageFields.AUTHORITIES, authorities);
  }

  public static setGroups(groups: string) {
    localStorage.setItem(LocalStorageFields.GROUPS, groups);
  }

  public static setApplications(groups: string) {
    localStorage.setItem(LocalStorageFields.APPLICATIONS, groups);
  }

  public static setMsalLoginHint(loginHint: string) {
    localStorage.setItem(LocalStorageFields.MSAL_LOGIN_HINT, loginHint);
  }

  public static setUserEmail(userEmail: string) {
    localStorage.setItem(LocalStorageFields.USER_EMAIL, userEmail);
  }

  public static setTheme(theme: string) {
    localStorage.setItem(LocalStorageFields.THEME, theme);
  }

  public static setUserSettingsId(userSettingsId: number) {
    localStorage.setItem(
      LocalStorageFields.USER_SETTINGS_ID,
      String(userSettingsId)
    );
  }

  public static getTheme(): string | null {
    return localStorage.getItem(LocalStorageFields.THEME);
  }

  public static getEmail(): string | null {
    return localStorage.getItem(LocalStorageFields.USER_EMAIL);
  }

  public static getAuthorities(): string[] | null {
    const authoritiesString: string = localStorage.getItem(
      LocalStorageFields.AUTHORITIES
    );
    return authoritiesString ? JSON.parse(authoritiesString) : null;
  }

  public static getGroups(): string[] | null {
    const groupsString: string = localStorage.getItem(
      LocalStorageFields.GROUPS
    );
    return groupsString ? JSON.parse(groupsString) : null;
  }

  public static getApplications(): string[] | null {
    const applicationsString: string = localStorage.getItem(
      LocalStorageFields.APPLICATIONS
    );
    return applicationsString ? JSON.parse(applicationsString) : null;
  }

  public static getMsalLoginHint() {
    return localStorage.getItem(LocalStorageFields.MSAL_LOGIN_HINT);
  }

  public static getUserSettingsId(): number | null {
    const userSettingsId = localStorage.getItem(
      LocalStorageFields.USER_SETTINGS_ID
    );
    if (userSettingsId) return Number(userSettingsId);
    return null;
  }

  public static setKeepAlive(keepAlive: string) {
    localStorage.setItem(LocalStorageFields.KEEP_ALIVE, keepAlive);
  }

  public static getKeepAlive(): string | null {
    return localStorage.getItem(LocalStorageFields.KEEP_ALIVE);
  }

  public static setChatbotAgent(agent: string) {
    if (!agent) {
      localStorage.removeItem(LocalStorageFields.CHATBOT_AGENT);
      return;
    }
    localStorage.setItem(LocalStorageFields.CHATBOT_AGENT, agent);
  }

  public static getChatbotAgent(): string | null {
    return localStorage.getItem(LocalStorageFields.CHATBOT_AGENT);
  }
}
